import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { MatrixRain } from './MatrixRain';
// Original product cover — the exact image buyers see at checkout.
const COVER_SRC = 'https://cdn.magicpatterns.com/uploads/nkB7M2PRaWx6YP7bo4V4QA/tapa.png';
const GOLD = '#d8b878';
// Lightweight ambient particles — positions are static so they don't
// reflow; only opacity/translate animate via framer-motion.
const PARTICLES = [{
  left: '12%',
  top: '24%',
  size: 3,
  delay: 0,
  gold: false
}, {
  left: '82%',
  top: '20%',
  size: 2,
  delay: 0.8,
  gold: true
}, {
  left: '24%',
  top: '66%',
  size: 2,
  delay: 1.4,
  gold: false
}, {
  left: '70%',
  top: '70%',
  size: 3,
  delay: 0.4,
  gold: true
}, {
  left: '50%',
  top: '16%',
  size: 2,
  delay: 1.1,
  gold: false
}, {
  left: '88%',
  top: '50%',
  size: 2,
  delay: 1.8,
  gold: false
}, {
  left: '8%',
  top: '46%',
  size: 2,
  delay: 0.6,
  gold: true
}, {
  left: '38%',
  top: '82%',
  size: 3,
  delay: 2.0,
  gold: false
}, {
  left: '62%',
  top: '38%',
  size: 2,
  delay: 1.6,
  gold: true
}, {
  left: '16%',
  top: '86%',
  size: 2,
  delay: 0.2,
  gold: false
}];
export function Hero() {
  const scrollToPrecio = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('precio');
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  // Subtle desktop parallax: track cursor position relative to the cover.
  const coverRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({
    rx: 0,
    ry: 0
  });
  const handleCoverMove = (e: React.MouseEvent) => {
    const node = coverRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({
      rx: py * -7,
      ry: px * 9
    });
  };
  const resetTilt = () => setTilt({
    rx: 0,
    ry: 0
  });
  return <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-matrix-black px-5 pt-16 md:pt-20 pb-32 md:pb-36" data-id="element-163">
      <MatrixRain data-id="element-164" />

      {/* Ambient depth: green core + warm gold portal glow + vignette */}
      <div className="pointer-events-none absolute inset-0 z-0" data-id="element-165">
        <div className="absolute left-1/2 top-1/2 h-[80vh] w-[80vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,_rgba(0,255,65,0.16)_0%,_rgba(0,255,65,0.05)_38%,_transparent_70%)] blur-2xl" data-id="element-166"></div>
        <div className="absolute left-1/2 top-[46%] h-[52vh] w-[52vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,_rgba(216,184,120,0.16)_0%,_rgba(216,184,120,0.05)_45%,_transparent_72%)] blur-2xl" data-id="element-167"></div>
        {/* lighter top wash so the codes read as depth, not a flat black wall */}
        <div className="absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(ellipse_at_top,_rgba(0,255,65,0.06)_0%,_transparent_60%)]" data-id="element-168"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_42%,_rgba(0,0,0,0.72)_100%)]" data-id="element-169"></div>
      </div>

      {/* Volumetric light beams — discreet, descend from the top */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden mix-blend-screen" data-id="element-170">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: [0.18, 0.32, 0.18]
      }} transition={{
        duration: 7,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute -top-1/4 left-[30%] h-[150%] w-[18vw] -rotate-[14deg] blur-2xl bg-[linear-gradient(180deg,_rgba(0,255,65,0.10)_0%,_transparent_75%)]" data-id="element-171" />
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: [0.12, 0.26, 0.12]
      }} transition={{
        duration: 9,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1.5
      }} className="absolute -top-1/4 left-[58%] h-[150%] w-[14vw] rotate-[12deg] blur-2xl bg-[linear-gradient(180deg,_rgba(216,184,120,0.10)_0%,_transparent_75%)]" data-id="element-172" />
      </div>

      {/* Floating ambient particles */}
      <div className="pointer-events-none absolute inset-0 z-0 hidden sm:block" data-id="element-173">
        {PARTICLES.map((p, i) => <motion.span key={i} className="absolute rounded-full" style={{
        left: p.left,
        top: p.top,
        width: p.size,
        height: p.size,
        backgroundColor: p.gold ? GOLD : '#00ff41',
        boxShadow: p.gold ? '0 0 8px rgba(216,184,120,0.8)' : '0 0 8px rgba(0,255,65,0.8)'
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.65, 0],
        y: [0, -18, 0]
      }} transition={{
        duration: 6,
        delay: p.delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-174" />)}
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center" data-id="element-175">
        {/* Top status badge */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="mb-9 md:mb-12 inline-flex items-center gap-2.5 rounded-full border border-matrix-green/25 bg-matrix-green/[0.06] px-4 py-1.5 backdrop-blur-sm" data-id="element-176">
          <span className="relative flex h-1.5 w-1.5" data-id="element-177">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-matrix-green opacity-75" data-id="element-178"></span>
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-matrix-green" data-id="element-179"></span>
          </span>
          <span className="font-inter text-[10px] md:text-xs tracking-[0.3em] uppercase text-matrix-green/90" data-id="element-180">
            Sistema · Sesión iniciada
          </span>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: 'easeOut'
      }} className="font-cinzel mb-7 md:mb-9 max-w-4xl flex flex-col items-center gap-1 md:gap-2" data-id="element-181">
          {/* Line 1 */}
          <span className="block text-[26px] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.18] font-bold text-matrix-cream drop-shadow-[0_0_22px_rgba(0,255,65,0.2)]" data-id="element-182">
            ¿Y si no estás{' '}
            <span className="italic font-medium text-matrix-white/55" data-id="element-183">
              perdido…
            </span>
          </span>
          {/* Line 2 — dominant gold */}
          <span className="block text-[30px] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] italic font-bold" style={{
          color: GOLD,
          textShadow: '0 0 30px rgba(216,184,120,0.4)'
        }} data-id="element-184">
            sino jugando un juego
          </span>
          {/* Line 3 — quieter resolution */}
          <span className="block text-[18px] sm:text-2xl md:text-3xl leading-[1.25] italic font-medium text-matrix-cream/80" data-id="element-185">
            que nunca te explicaron?
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="font-inter text-[15px] md:text-lg text-matrix-white/90 max-w-xl mb-5 md:mb-6 font-light leading-relaxed" data-id="element-186">
          Un ebook profundo para cuestionar las creencias que han condicionado
          tu vida, reconocer los patrones que te mantienen en automático y
          empezar a mirar tu realidad con más conciencia, claridad y poder
          interior.
        </motion.p>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.32,
        ease: 'easeOut'
      }} className="font-inter text-[12.5px] md:text-sm text-matrix-white/65 max-w-lg mb-9 md:mb-11 font-light italic leading-relaxed" data-id="element-187">
          No es solo una lectura. Es una invitación a detenerte, mirar el
          sistema con otros ojos y recordar que no eres únicamente el personaje
          que aprendiste a ser. Eres el jugador.
        </motion.p>

        {/* Package inclusion line — sits within the immersive tone */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.42,
        ease: 'easeOut'
      }} className="mb-12 md:mb-16 inline-flex max-w-xl items-center gap-3 rounded-full border border-[rgba(216,184,120,0.28)] bg-matrix-dark/40 backdrop-blur-sm px-5 py-2.5" data-id="element-188">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{
          backgroundColor: GOLD,
          boxShadow: '0 0 10px rgba(216,184,120,0.8)'
        }} data-id="element-189"></span>
          <span className="font-inter text-[11.5px] md:text-sm font-light leading-snug text-matrix-cream/90" data-id="element-190">
            Incluye ebook principal, cuaderno de reflexión y acceso interactivo
            protegido.
          </span>
        </motion.div>

        {/* Cover — focal point inside a golden portal, pseudo-3D + parallax */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.1,
        delay: 0.45,
        ease: 'easeOut'
      }} className="relative mb-12 md:mb-16 flex flex-col items-center" style={{
        perspective: 1000
      }} data-id="element-191">
          {/* Vertical oval portal / arch behind the cover — stronger, editorial */}
          <div className="pointer-events-none absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2 h-[128%] w-[150%] sm:w-[135%]" data-id="element-192">
            {/* Soft oval glow body */}
            <div className="absolute inset-0 rounded-[50%] bg-[radial-gradient(ellipse_at_center,_rgba(216,184,120,0.30)_0%,_rgba(0,255,65,0.14)_42%,_transparent_70%)] blur-2xl" data-id="element-193"></div>
            {/* Arch outline */}
            <div className="absolute inset-[6%] rounded-[48%] border border-[rgba(216,184,120,0.30)] shadow-[0_0_60px_rgba(216,184,120,0.15)_inset]" data-id="element-194"></div>
            <div className="absolute inset-[12%] rounded-[48%] border border-[rgba(216,184,120,0.12)]" data-id="element-195"></div>
            {/* Breathing halo ring — soft pulse of light behind the book */}
            <motion.div className="absolute inset-[9%] rounded-[48%] border-2 border-[rgba(216,184,120,0.35)]" style={{
            boxShadow: '0 0 50px rgba(216,184,120,0.35)'
          }} animate={{
            opacity: [0.25, 0.65, 0.25],
            scale: [0.97, 1.02, 0.97]
          }} transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-196" />
          </div>

          <a href="#precio" onClick={scrollToPrecio} className="relative inline-block cursor-pointer" data-id="element-197">
            <motion.div ref={coverRef} onMouseMove={handleCoverMove} onMouseLeave={resetTilt} animate={{
            rotateX: tilt.rx,
            rotateY: tilt.ry
          }} transition={{
            type: 'spring',
            stiffness: 120,
            damping: 12
          }} style={{
            transformStyle: 'preserve-3d'
          }} className="relative animate-float" data-id="element-198">
              <div className="relative z-10 w-60 sm:w-72 md:w-80 lg:w-[26rem] overflow-hidden rounded-xl border border-[rgba(216,184,120,0.35)] shadow-[0_45px_90px_-20px_rgba(0,0,0,0.95),0_0_70px_rgba(216,184,120,0.30)]" data-id="element-199">
                <img src={COVER_SRC} alt="Ebook: ¿Estás listo para jugar?" className="block w-full" data-id="element-200" />
                {/* Vertical sheen — an elegant band of light gliding up & down */}
                <motion.div className="pointer-events-none absolute inset-x-0 h-1/3 mix-blend-screen" style={{
                background: 'linear-gradient(180deg, transparent 0%, rgba(255,245,215,0.18) 50%, transparent 100%)'
              }} initial={{
                top: '-35%'
              }} animate={{
                top: ['-35%', '100%', '-35%']
              }} transition={{
                duration: 7,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-201" />
              </div>
            </motion.div>
          </a>

          {/* Premium staging caption + glass pedestal */}
          <div className="pointer-events-none relative z-10 mt-7 md:mt-8 flex flex-col items-center" data-id="element-202">
            <p className="font-inter text-[10px] md:text-xs tracking-[0.35em] uppercase mb-4" style={{
            color: 'rgba(216,184,120,0.7)'
          }} data-id="element-203">
              Ebook · 118 páginas · PDF
            </p>
            <div className="h-px w-40 sm:w-52 md:w-64 bg-gradient-to-r from-transparent via-[rgba(216,184,120,0.55)] to-transparent" data-id="element-204"></div>
            <div className="mt-3 h-10 w-48 sm:w-60 md:w-72 rounded-[50%] border border-white/10 bg-white/[0.03] backdrop-blur-md shadow-[0_-6px_30px_-10px_rgba(0,255,65,0.35)]" data-id="element-205"></div>
          </div>
        </motion.div>

        {/* Premium gold CTA — refined, not harsh neon */}
        <motion.a href="#precio" onClick={scrollToPrecio} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} whileHover={{
        scale: 1.04,
        boxShadow: '0 0 38px rgba(216,184,120,0.55)'
      }} whileTap={{
        scale: 0.96
      }} style={{
        backgroundImage: 'linear-gradient(135deg, #e8cf9a 0%, #d8b878 50%, #c4a05f 100%)',
        color: '#0a0f0a'
      }} className="inline-block font-inter font-bold text-[15px] md:text-lg px-10 md:px-14 py-4 md:py-5 rounded-md uppercase tracking-[0.12em] transition-all duration-300 shadow-[0_0_28px_rgba(216,184,120,0.4)] border border-[rgba(255,245,220,0.4)]" data-id="element-206">
          QUIERO MI EBOOK + BONO
        </motion.a>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9,
        duration: 0.8
      }} className="mt-6 md:mt-7 font-inter text-matrix-cream/75 text-[11px] md:text-sm font-light tracking-[0.12em] uppercase" data-id="element-207">
          Ebook digital + bono incluido + acceso interactivo protegido
        </motion.p>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.05,
        duration: 0.8
      }} className="mt-3 font-inter text-matrix-white/50 text-[10px] md:text-xs font-light tracking-[0.15em] uppercase" data-id="element-208">
          Acceso inmediato · Compra segura
        </motion.p>
      </div>

      {/* Scroll cue — separated from microcopy */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} className="absolute bottom-8 md:bottom-10 z-10 flex flex-col items-center gap-2 text-matrix-green/70" data-id="element-209">
        <span className="font-inter text-[9.5px] md:text-[11px] tracking-[0.3em] uppercase text-matrix-white/45" data-id="element-210">
          Desciende y observa el tablero
        </span>
        <motion.div animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-211">
          <ChevronDown size={26} className="opacity-70" data-id="element-212" />
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-matrix-dark to-transparent z-0" data-id="element-213"></div>
    </section>;
}